// Smart Daara - Super Admin Dashboard
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { tenantAPI, subscriptionAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import { 
  Building2, Users, BookOpen, CreditCard, TrendingUp, Plus,
  ArrowUpRight, MoreVertical, Search, Filter, Trash2, Power,
  PowerOff, Eye, Calendar, AlertTriangle, Loader2, CheckCircle, Clock
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function SuperAdminDashboard() {
  const { t, language, getText } = useLanguage();
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, expired: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state for creating tenant
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description_fr: '',
    contact_email: '',
    contact_phone: '',
    is_free: false,
    school_type: 'virtual',
    subscription_plan: 'starter',
    admin_email: '',
    admin_first_name: '',
    admin_last_name: '',
    admin_password: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await tenantAPI.list({ page: 1, page_size: 100 });
      setTenants(response.data.items || []);
      
      // Calculate stats
      const items = response.data.items || [];
      setStats({
        total: response.data.total || 0,
        active: items.filter(t => t.subscription_status === 'active' && t.is_active).length,
        pending: items.filter(t => t.subscription_status === 'pending').length,
        expired: items.filter(t => t.subscription_status === 'expired' || !t.is_active).length
      });
    } catch (error) {
      console.error('Error loading tenants:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Auto-generate slug from name
      if (field === 'name') {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const handleCreateTenant = async () => {
    if (!formData.name || !formData.slug) {
      toast.error(language === 'fr' ? 'Le nom est requis' : 'Name is required');
      return;
    }

    if (!formData.is_free && (!formData.admin_email || !formData.admin_password)) {
      toast.error(language === 'fr' 
        ? 'Email et mot de passe admin requis pour les écoles payantes'
        : 'Admin email and password required for paid schools');
      return;
    }

    setSaving(true);
    try {
      await tenantAPI.create(formData);
      toast.success(language === 'fr' ? 'École créée avec succès!' : 'School created successfully!');
      setCreateModalOpen(false);
      setFormData({
        name: '', slug: '', description_fr: '', contact_email: '', contact_phone: '',
        is_free: false, school_type: 'virtual', subscription_plan: 'starter',
        admin_email: '', admin_first_name: '', admin_last_name: '', admin_password: ''
      });
      loadData();
    } catch (error) {
      console.error('Error creating tenant:', error);
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de création' : 'Creation error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateTenant = async (tenant) => {
    try {
      await tenantAPI.delete(tenant.id);
      toast.success(language === 'fr' ? 'École désactivée' : 'School deactivated');
      loadData();
    } catch (error) {
      console.error('Error deactivating tenant:', error);
      toast.error(language === 'fr' ? 'Erreur de désactivation' : 'Deactivation error');
    }
  };

  const [importingTenant, setImportingTenant] = useState(null);

  const handleImportProgramme = async (tenant) => {
    if (!window.confirm(language === 'fr'
      ? `Importer le programme primaire (CP → CM2) dans « ${tenant.name} » et fusionner l'ancien contenu ?`
      : `Import the primary programme (CP → CM2) into "${tenant.name}" and merge legacy content?`)) return;
    setImportingTenant(tenant.id);
    try {
      const res = await tenantAPI.importProgramme(tenant.id);
      toast.success(res.data?.message_fr || res.data?.message || 'Import terminé');
    } catch (error) {
      console.error('Error importing programme:', error);
      toast.error(error.response?.data?.detail || (language === 'fr' ? "Erreur lors de l'import" : 'Import error'));
    } finally {
      setImportingTenant(null);
    }
  };

  const handleActivateTenant = async (tenant) => {
    try {
      await tenantAPI.activate(tenant.id);
      toast.success(language === 'fr' ? 'École activée' : 'School activated');
      loadData();
    } catch (error) {
      console.error('Error activating tenant:', error);
      toast.error(language === 'fr' ? 'Erreur d\'activation' : 'Activation error');
    }
  };

  const handleDeletePermanently = async () => {
    if (!selectedTenant) return;
    
    setSaving(true);
    try {
      await tenantAPI.deletePermanently(selectedTenant.id, true);
      toast.success(language === 'fr' ? 'École supprimée définitivement' : 'School permanently deleted');
      setDeleteModalOpen(false);
      setSelectedTenant(null);
      loadData();
    } catch (error) {
      console.error('Error deleting tenant:', error);
      toast.error(language === 'fr' ? 'Erreur de suppression' : 'Delete error');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
        day: 'numeric', month: 'short', year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const getDaysRemaining = (deactivationDate) => {
    if (!deactivationDate) return null;
    try {
      const date = new Date(deactivationDate);
      const now = new Date();
      const diff = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
      return diff;
    } catch {
      return null;
    }
  };

  const filteredTenants = tenants.filter(t => 
    (t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.slug?.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (typeFilter === 'all' || (t.school_type || 'virtual') === typeFilter)
  );

  const statsCards = [
    {
      title: language === 'fr' ? 'Total Écoles' : 'Total Schools',
      value: stats.total,
      icon: Building2,
      color: 'bg-emerald-100 text-emerald-700'
    },
    {
      title: language === 'fr' ? 'Actifs' : 'Active',
      value: stats.active,
      icon: CheckCircle,
      color: 'bg-green-100 text-green-700'
    },
    {
      title: language === 'fr' ? 'En Attente' : 'Pending',
      value: stats.pending,
      icon: Clock,
      color: 'bg-amber-100 text-amber-700'
    },
    {
      title: language === 'fr' ? 'Expirés/Désactivés' : 'Expired/Deactivated',
      value: stats.expired,
      icon: PowerOff,
      color: 'bg-red-100 text-red-700'
    }
  ];

  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-slate-900">
            {t('dashboard')} Super Admin
          </h1>
          <p className="text-slate-500">
            {language === 'fr' ? 'Gérez toutes les écoles et abonnements' : 'Manage all schools and subscriptions'}
          </p>
        </div>
        <Button 
          className="bg-emerald-700 hover:bg-emerald-800" 
          data-testid="create-tenant-btn"
          onClick={() => setCreateModalOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          {language === 'fr' ? 'Nouvelle École' : 'New School'}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsCards.map((stat, index) => (
          <Card key={index} className="border-0 shadow-sm" data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-4">
                <p className="text-2xl font-outfit font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500 mt-1">{stat.title}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tenants Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <div>
            <CardTitle className="font-outfit">
              {language === 'fr' ? 'Écoles' : 'Schools'}
            </CardTitle>
            <CardDescription>
              {language === 'fr' ? 'Liste de toutes les écoles enregistrées' : 'List of all registered schools'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <select className="h-10 border border-slate-200 rounded-md px-2 text-sm" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="tenant-type-filter">
              <option value="all">{language === 'fr' ? 'Tous types' : 'All types'}</option>
              <option value="virtual">{language === 'fr' ? 'Virtuelles' : 'Virtual'}</option>
              <option value="physical">{language === 'fr' ? 'Physiques' : 'Physical'}</option>
            </select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder={t('search')} 
                className="pl-9 w-64" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">
                      {language === 'fr' ? 'École' : 'School'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">
                      Plan
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">
                      Status
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">
                      {language === 'fr' ? 'Expiration' : 'Expiration'}
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTenants.map((tenant) => {
                    const daysRemaining = getDaysRemaining(tenant.deactivation_date);
                    const isExpiringSoon = daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 0;
                    const isExpired = daysRemaining !== null && daysRemaining <= 0;
                    
                    return (
                      <tr key={tenant.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              tenant.is_active ? 'bg-emerald-100' : 'bg-slate-100'
                            }`}>
                              <Building2 className={`w-5 h-5 ${
                                tenant.is_active ? 'text-emerald-700' : 'text-slate-400'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{tenant.name}</p>
                              <div className="flex items-center gap-1.5">
                                <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{tenant.slug}</code>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                  (tenant.school_type || 'virtual') === 'physical' ? 'bg-sky-100 text-sky-700' : 'bg-emerald-50 text-emerald-700'
                                }`} data-testid={`tenant-type-${tenant.slug}`}>
                                  {(tenant.school_type || 'virtual') === 'physical'
                                    ? (language === 'fr' ? 'Physique' : 'Physical')
                                    : (language === 'fr' ? 'Virtuelle' : 'Virtual')}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`text-sm px-2 py-1 rounded ${
                            tenant.subscription_plan === 'enterprise' 
                              ? 'bg-purple-100 text-purple-700'
                              : tenant.subscription_plan === 'pro'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-slate-100 text-slate-700'
                          }`}>
                            {tenant.subscription_plan?.charAt(0).toUpperCase() + tenant.subscription_plan?.slice(1)}
                          </span>
                          {tenant.is_free && (
                            <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                              {language === 'fr' ? 'Gratuit' : 'Free'}
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <span className={`inline-flex items-center gap-1.5 text-sm px-2 py-1 rounded ${
                            tenant.is_active && tenant.subscription_status === 'active'
                              ? 'bg-emerald-100 text-emerald-700'
                              : tenant.subscription_status === 'pending'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              tenant.is_active && tenant.subscription_status === 'active' 
                                ? 'bg-emerald-500' 
                                : tenant.subscription_status === 'pending' 
                                ? 'bg-amber-500' 
                                : 'bg-red-500'
                            }`} />
                            {!tenant.is_active ? (language === 'fr' ? 'Désactivé' : 'Deactivated') : tenant.subscription_status}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          {tenant.is_free ? (
                            <span className="text-sm text-emerald-600">
                              {language === 'fr' ? 'Jamais' : 'Never'}
                            </span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className={`text-sm ${
                                isExpired ? 'text-red-600 font-medium' :
                                isExpiringSoon ? 'text-amber-600' : 'text-slate-600'
                              }`}>
                                {formatDate(tenant.deactivation_date)}
                              </span>
                              {isExpiringSoon && !isExpired && (
                                <AlertTriangle className="w-4 h-4 text-amber-500" />
                              )}
                              {isExpired && (
                                <Badge variant="destructive" className="text-xs">
                                  {language === 'fr' ? 'Expiré' : 'Expired'}
                                </Badge>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="py-4 px-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem>
                                <Eye className="w-4 h-4 mr-2" />
                                {language === 'fr' ? 'Voir détails' : 'View Details'}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleImportProgramme(tenant)}
                                disabled={importingTenant === tenant.id}
                                data-testid={`import-programme-${tenant.id}`}
                              >
                                <BookOpen className="w-4 h-4 mr-2" />
                                {importingTenant === tenant.id
                                  ? (language === 'fr' ? 'Import en cours...' : 'Importing...')
                                  : (language === 'fr' ? 'Importer programme primaire' : 'Import primary programme')}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {tenant.is_active ? (
                                <DropdownMenuItem 
                                  onClick={() => handleDeactivateTenant(tenant)}
                                  className="text-amber-600"
                                >
                                  <PowerOff className="w-4 h-4 mr-2" />
                                  {language === 'fr' ? 'Désactiver' : 'Deactivate'}
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem 
                                  onClick={() => handleActivateTenant(tenant)}
                                  className="text-emerald-600"
                                >
                                  <Power className="w-4 h-4 mr-2" />
                                  {language === 'fr' ? 'Activer' : 'Activate'}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedTenant(tenant);
                                  setDeleteModalOpen(true);
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                {language === 'fr' ? 'Supprimer définitivement' : 'Delete Permanently'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredTenants.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  {language === 'fr' ? 'Aucune école trouvée' : 'No schools found'}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Tenant Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Créer une nouvelle école' : 'Create a new school'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Remplissez les informations de l\'école et créez un administrateur'
                : 'Fill in the school information and create an administrator'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* School Info Section */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-900 border-b pb-2">
                {language === 'fr' ? 'Informations de l\'école' : 'School Information'}
              </h3>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Type d\'école' : 'School type'} *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button"
                    className={`text-left border rounded-lg p-3 transition-colors ${formData.school_type === 'virtual' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}
                    onClick={() => handleFormChange('school_type', 'virtual')}
                    data-testid="school-type-virtual">
                    <p className="font-medium text-sm">{language === 'fr' ? 'École virtuelle (digitale)' : 'Virtual school (digital)'}</p>
                    <p className="text-xs text-slate-500 mt-1">{language === 'fr' ? 'Cours en ligne, vidéos, PDF, quiz, progression' : 'Online courses, videos, PDFs, quizzes, progress'}</p>
                  </button>
                  <button type="button"
                    className={`text-left border rounded-lg p-3 transition-colors ${formData.school_type === 'physical' ? 'border-sky-600 bg-sky-50' : 'border-slate-200 hover:border-slate-300'}`}
                    onClick={() => handleFormChange('school_type', 'physical')}
                    data-testid="school-type-physical">
                    <p className="font-medium text-sm">{language === 'fr' ? 'École physique' : 'Physical school'}</p>
                    <p className="text-xs text-slate-500 mt-1">{language === 'fr' ? 'Gestion d\'établissement : classes, emplois du temps, notes, bulletins' : 'School management: classes, timetables, grades, report cards'}</p>
                  </button>
                </div>
                <p className="text-xs text-amber-600">{language === 'fr' ? '⚠️ Le type est verrouillé après création.' : '⚠️ Type is locked after creation.'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Nom de l\'école' : 'School Name'} *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    placeholder="Ex: École Primaire Dakar"
                    data-testid="tenant-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug (URL) *</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => handleFormChange('slug', e.target.value)}
                    placeholder="ecole-primaire-dakar"
                    data-testid="tenant-slug"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Description' : 'Description'}</Label>
                <Textarea
                  value={formData.description_fr}
                  onChange={(e) => handleFormChange('description_fr', e.target.value)}
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleFormChange('contact_email', e.target.value)}
                    placeholder="contact@ecole.sn"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => handleFormChange('contact_phone', e.target.value)}
                    placeholder="+221 77 000 0000"
                  />
                </div>
              </div>
            </div>

            {/* Subscription Section */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-900 border-b pb-2">
                {language === 'fr' ? 'Abonnement' : 'Subscription'}
              </h3>
              
              <div className="flex items-center gap-4">
                <Switch
                  checked={formData.is_free}
                  onCheckedChange={(checked) => handleFormChange('is_free', checked)}
                  data-testid="tenant-is-free"
                />
                <Label>
                  {language === 'fr' ? 'École gratuite (pas de date d\'expiration)' : 'Free school (no expiration date)'}
                </Label>
              </div>

              {!formData.is_free && (
                <div className="space-y-2">
                  <Label>Plan</Label>
                  <Select 
                    value={formData.subscription_plan} 
                    onValueChange={(value) => handleFormChange('subscription_plan', value)}
                  >
                    <SelectTrigger data-testid="tenant-plan">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">Starter - 30,000 FCFA/mois</SelectItem>
                      <SelectItem value="pro">Pro - 60,000 FCFA/mois</SelectItem>
                      <SelectItem value="enterprise">Enterprise - 100,000 FCFA/mois</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {language === 'fr' 
                      ? 'L\'école aura 30 jours pour payer et activer l\'abonnement'
                      : 'The school will have 30 days to pay and activate the subscription'
                    }
                  </p>
                </div>
              )}
            </div>

            {/* Admin Section */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-900 border-b pb-2">
                {language === 'fr' ? 'Administrateur de l\'école' : 'School Administrator'}
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Prénom' : 'First Name'}</Label>
                  <Input
                    value={formData.admin_first_name}
                    onChange={(e) => handleFormChange('admin_first_name', e.target.value)}
                    placeholder="Jean"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Nom' : 'Last Name'}</Label>
                  <Input
                    value={formData.admin_last_name}
                    onChange={(e) => handleFormChange('admin_last_name', e.target.value)}
                    placeholder="Dupont"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email Admin {!formData.is_free && '*'}</Label>
                  <Input
                    type="email"
                    value={formData.admin_email}
                    onChange={(e) => handleFormChange('admin_email', e.target.value)}
                    placeholder="admin@ecole.sn"
                    data-testid="admin-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Mot de passe' : 'Password'} {!formData.is_free && '*'}</Label>
                  <Input
                    type="password"
                    value={formData.admin_password}
                    onChange={(e) => handleFormChange('admin_password', e.target.value)}
                    placeholder="••••••••"
                    data-testid="admin-password"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreateTenant}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="save-tenant-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Créer l\'école' : 'Create School'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {language === 'fr' ? 'Suppression définitive' : 'Permanent Deletion'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? `Êtes-vous sûr de vouloir supprimer définitivement "${selectedTenant?.name}"? Cette action supprimera toutes les données associées (utilisateurs, cours, leçons, etc.) et ne peut pas être annulée.`
                : `Are you sure you want to permanently delete "${selectedTenant?.name}"? This will delete all associated data (users, courses, lessons, etc.) and cannot be undone.`
              }
            </DialogDescription>
          </DialogHeader>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
            <p className="text-red-800 text-sm font-medium">
              {language === 'fr' ? 'Données qui seront supprimées:' : 'Data that will be deleted:'}
            </p>
            <ul className="text-red-700 text-sm mt-2 space-y-1">
              <li>• {language === 'fr' ? 'Tous les utilisateurs (admins, profs, élèves, parents)' : 'All users (admins, teachers, students, parents)'}</li>
              <li>• {language === 'fr' ? 'Toutes les classes et matières' : 'All classes and subjects'}</li>
              <li>• {language === 'fr' ? 'Tous les cours et leçons' : 'All courses and lessons'}</li>
              <li>• {language === 'fr' ? 'Toutes les progressions et certificats' : 'All progress and certificates'}</li>
              <li>• {language === 'fr' ? 'Tous les paiements et abonnements' : 'All payments and subscriptions'}</li>
            </ul>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleDeletePermanently}
              disabled={saving}
              variant="destructive"
              data-testid="confirm-delete-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Supprimer définitivement' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
